import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { types, utils } from "@vilocnv/allsetra-core";
import { useAppDispatch, useAppSelector } from "./reduxHooks";
import { selectObjectRidesState } from "app/data/selectors";
import { updateObjectRidesSilently } from "app/features";
import { ObjectsService } from "app/data/services";

interface UseSilentRidesRefreshOptions {
  accountId: string;
  objectId: string;
  maxRetries?: number;
  delayMs?: number;
}

interface UseSilentRidesRefreshReturn {
  silentRefreshRides: (rideIdsToCheck?: string[]) => Promise<void>;
}

/**
 * Hook for silently refreshing rides data without triggering global loading state.
 * Uses a retry mechanism to ensure data is actually updated before clearing loading state.
 *
 * @param options - Configuration options
 * @param options.accountId - The account ID
 * @param options.objectId - The object ID
 * @param options.maxRetries - Maximum number of retry attempts (default: 3)
 * @param options.delayMs - Delay between retries in milliseconds (default: 1000)
 */
export const useSilentRidesRefresh = ({
  accountId,
  objectId,
  maxRetries = 3,
  delayMs = 1000
}: UseSilentRidesRefreshOptions): UseSilentRidesRefreshReturn => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { objectRides } = useAppSelector(selectObjectRidesState);

  const silentRefreshRides = useCallback(
    async (rideIdsToCheck?: string[]) => {
      if (!objectId) return;

      const fetchRides = async () => {
        const page = parseInt(searchParams.get("page") || "1", 10);
        const itemsPerPage = parseInt(
          searchParams.get("itemsPerPage") ||
            String(utils.getCommonParamsForApi().itemsPerPage || 10),
          10
        );
        const startDate = searchParams.get("startDate");
        const endDate = searchParams.get("endDate");

        const params: any = {
          page,
          itemsPerPage,
          orderBy: [{ field: "startDate", ascending: true }],
          includeRidesWithoutDriver: true,
          startedWithoutIgnition: true
        };

        if (startDate) {
          params.startDate = startDate;
        }
        if (endDate) {
          params.endDate = endDate;
        }

        return ObjectsService.getObjectRidesByQuery(
          { accountId, objectId },
          params
        );
      };

      const hasRideDataChanged = (
        oldRide: types.IObjectRide,
        newRide: types.IObjectRide
      ): boolean => {
        return (
          oldRide.rideMode !== newRide.rideMode ||
          (oldRide as any).driverId !== (newRide as any).driverId ||
          (oldRide as any).keyId !== (newRide as any).keyId ||
          (oldRide as any).detourMileage !== (newRide as any).detourMileage ||
          (oldRide as any).privateMileage !== (newRide as any).privateMileage
        );
      };

      try {
        let retryCount = 0;
        let dataChanged = false;

        while (retryCount < maxRetries && !dataChanged) {
          // Add delay before fetching (always delay when checking specific IDs)
          if (retryCount > 0 || (rideIdsToCheck && rideIdsToCheck.length > 0)) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }

          const response = await fetchRides();

          if (response.status === 200) {
            const newRides = response.data.results || [];

            if (rideIdsToCheck && rideIdsToCheck.length > 0) {
              const currentRidesMap = new Map(
                objectRides.map((r) => [r.uniqueId, r])
              );

              // Check if any of the target rides have different data
              dataChanged = rideIdsToCheck.some((rideId) => {
                const oldRide = currentRidesMap.get(rideId);
                const newRide = newRides.find(
                  (r: types.IObjectRide) => r.uniqueId === rideId
                );

                if (!oldRide || !newRide) return true;
                return hasRideDataChanged(oldRide, newRide);
              });

              // Update Redux if data changed or on last retry
              if (dataChanged || retryCount === maxRetries - 1) {
                dispatch(
                  updateObjectRidesSilently({
                    results: newRides,
                    rowCount: response.data.rowCount || 0
                  })
                );
              }
            } else {
              // No specific IDs to check, just update immediately
              dispatch(
                updateObjectRidesSilently({
                  results: newRides,
                  rowCount: response.data.rowCount || 0
                })
              );
              dataChanged = true;
            }
          }

          retryCount++;
        }
      } catch (error) {
        console.error("Error fetching rides silently:", error);
      }
    },
    [
      accountId,
      objectId,
      dispatch,
      searchParams,
      objectRides,
      maxRetries,
      delayMs
    ]
  );

  return { silentRefreshRides };
};

export default useSilentRidesRefresh;
